/**
 * Category-moderator gating for the forum UI (FEA-29/FEA-30,
 * docs/FRONTEND-PLAN.md §4.3). The backend widened `isModerator(user,
 * category)` to `staff OR category.moderators.includes(user.userId)`; the
 * frontend's moderation controls (lock/pin/delete thread, delete any post,
 * the non-admin searchability toggle) gated on `isStaff` alone and need the
 * same widening.
 *
 * `GET /forum/threads/{threadId}` returns the thread only — no category
 * (verified in src/services/forum/index.js handleGetThread) — and there is
 * no single-category GET endpoint. `GET /forum/categories` (every category
 * the caller may see, moderators included via ForumCategory.toPublicJSON)
 * is the only place a client can read `moderators`, so pages that need it
 * for one category fetch that list and match by id. The category count is
 * small; this is a light one-shot fetch, not a page-weight concern.
 */
import { useEffect, useState } from 'react';
import * as forumApi from '../api/forum';

/**
 * @param {string|undefined|null} categoryId
 * @param {{userId: string|undefined, isStaff: boolean}} who
 * @returns {{category: object|null, isModerator: boolean}}
 */
export function useCategoryModerator(categoryId, { userId, isStaff }) {
  const [category, setCategory] = useState(null);

  useEffect(() => {
    if (!categoryId) {
      setCategory(null);
      return undefined;
    }
    let live = true;
    forumApi
      .listCategories()
      .then((d) => {
        if (!live) return;
        setCategory((d.categories || []).find((c) => c.categoryId === categoryId) || null);
      })
      .catch(() => {
        if (live) setCategory(null);
      });
    return () => {
      live = false;
    };
  }, [categoryId]);

  const isModerator =
    isStaff || Boolean(category && userId && (category.moderators || []).includes(userId));

  return { category, isModerator };
}

/** Same check when the category object is already in hand (e.g. Forum.jsx's category list). */
export function isCategoryModerator(category, { userId, isStaff }) {
  return isStaff || Boolean(category && userId && (category.moderators || []).includes(userId));
}
